import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Silence the benign "ResizeObserver loop completed with undelivered
// notifications" error fired by Radix Select/Tabs in CRA's dev overlay.
const RO_MSG = "ResizeObserver loop";
window.addEventListener("error", (e) => {
  if (e?.message && e.message.includes(RO_MSG)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register the offline service worker (skip in dev/HMR to avoid stale shells).
// v3 of the SW (Feb 2026) is network-first so deploys propagate to Safari
// immediately. We also poll for SW updates every 60s and auto-reload the
// page once when a new SW takes control, so the user doesn't have to know
// what a service worker is to recover from a stale bundle.
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        // Poll for updates while the tab is open.
        setInterval(() => { reg.update().catch(() => {}); }, 60_000);
        // If a new SW finishes installing while the tab is open, ask it to
        // skipWaiting so it activates immediately.
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              nw.postMessage("SW_SKIP_WAITING");
            }
          });
        });
      })
      .catch(() => { /* swallow */ });

    // When a new SW takes control of the page, reload exactly once so the
    // user sees the fresh JS/HTML without needing to know about caches.
    let didReload = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (didReload) return;
      didReload = true;
      window.location.reload();
    });
  });
}
